import { Tabs } from '@design-systems/canvas-ui'
import { useState } from 'react'
import './item-search-chart.scss'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { getYDomainForForecast } from './helpers'
import { useLogClick } from 'utils/analytics'

type ItemSearchChartProps = {
    item_details: any
}

export const ItemSearchChart = ({ item_details }: ItemSearchChartProps) => {
    const { t } = useEssentials()

    const { itemSearch } = TRANSLATION_KEYS
    const { tabs } = itemSearch
    const { actual, forecast } = tabs
    const [activeTab, setActiveTab] = useState<string>(actual)

    const { logClick } = useLogClick('itemSearch')

    if (item_details === undefined) {
        return <></>
    }
    const { sales_history, store_sales_forecast } = item_details
    if (!sales_history?.sales_history_average && !store_sales_forecast) {
        return <></>
    }

    const { sales_history_average } = sales_history
    const selectedDetails =
        activeTab === actual ? sales_history_average : store_sales_forecast

    if (!selectedDetails?.location_details || !selectedDetails?.time_line) {
        return <></>
    }

    const { location_details, time_line } = selectedDetails
    const allQuantities: number[] = []
    const chartData = time_line.map((time: string, index: number) => {
        const dataPoint: Record<string, any> = { name: time }

        location_details.forEach((location: any) => {
            const quantity =
                location.time_lines_quantity?.[index]?.quantity || 0
            const numericQuantity = Number(quantity)
            dataPoint[location.location] = numericQuantity
            if (!isNaN(numericQuantity) && isFinite(numericQuantity)) {
                allQuantities.push(numericQuantity)
            }
        })

        return dataPoint
    })

    const yAxisMax = allQuantities.length > 0 ? Math.max(...allQuantities) : 1

    const [minYForecast, maxYForecast] = getYDomainForForecast(chartData)

    const handleTabSelect = (activeTab: string) => {
        setActiveTab(activeTab)
        logClick('item search tab ' + activeTab)
    }

    const forecastStore = store_sales_forecast.location_details[0]
    const forecastStoreQuantity = forecastStore.time_lines_quantity.reduce(
        (acc: number, item: any) => {
            return acc + Number(item.quantity)
        },
        0,
    )

    const isTabsRequired = sales_history_average && forecastStoreQuantity > 0

    return (
        <>
            {isTabsRequired && (
                <Tabs
                    className={'kpi'}
                    activeTab={activeTab}
                    onTabSelect={handleTabSelect}
                    disableAdaptive={true}
                >
                    <Tabs.Item name={actual}>{t(actual)}</Tabs.Item>
                    <Tabs.Item name={forecast}>{t(forecast)}</Tabs.Item>
                </Tabs>
            )}
            <ResponsiveContainer width={'100%'} height={300}>
                <LineChart
                    data={chartData}
                    {...{
                        overflow: 'visible',
                        margin: {
                            left: -20,
                            right: 40,
                            top: 40,
                        },
                    }}
                >
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: '14px' }}
                        interval={0}
                        angle={-90}
                        height={95}
                        dy={45}
                    />
                    <YAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontSize: '14px' }}
                        domain={
                            activeTab === actual
                                ? [0, yAxisMax]
                                : [minYForecast, maxYForecast]
                        }
                    />
                    <Tooltip />
                    <Legend
                        content={() => (
                            <ul className="recharts-default-legend item-search-chart__legend">
                                {location_details.map((locationDetail: any) => (
                                    <li
                                        key={locationDetail.location}
                                        className="recharts-legend-item item-search-chart__legend-item"
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            className="item-search-chart__legend-icon"
                                        >
                                            <line
                                                x1="0"
                                                y1="7"
                                                x2="14"
                                                y2="7"
                                                stroke={locationDetail.color}
                                                strokeWidth="2"
                                            />
                                        </svg>
                                        <span
                                            className="recharts-legend-item-text"
                                            style={{
                                                color: locationDetail.color,
                                            }}
                                        >
                                            {locationDetail.location}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        wrapperStyle={{
                            left: 0,
                            margin: '0 auto',
                            minWidth: '100%',
                            maxWidth: '100%',
                        }}
                    />
                    <CartesianGrid stroke="#dedede" strokeDasharray="5 5" />
                    {location_details.map((locationDetail: any) => (
                        <Line
                            key={locationDetail.location}
                            type="monotone"
                            dataKey={locationDetail.location}
                            stroke={locationDetail.color}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </>
    )
}
